<template>
  <div class="rgf">
    <!-- loading -->
    <div v-if="loading" class="rgf-grid">
      <div v-for="n in 3" :key="n" class="rgf-card rgf-card--skeleton">
        <div class="rgf-card__media"/>
        <div class="rgf-skel rgf-skel--title"/>
        <div class="rgf-skel rgf-skel--text"/>
      </div>
    </div>

    <!-- nothing assigned yet -->
    <div v-else-if="selectedFeatures.length === 0" class="rgf-empty">
      <div class="rgf-empty__icon"><i class="fa-solid fa-bell-concierge"></i></div>
      <p class="rgf-empty__title">{{ msg('noguestproductsyet') }}</p>
      <p class="rgf-empty__hint">{{ msg('guestproductshint') }}</p>
      <b-button variant="primary" size="sm" class="rgf-btn" @click="addNewFeature">
        <i class="fas fa-plus mr-50"></i>{{ msg('Add feature') }}
      </b-button>
    </div>

    <!-- assigned products -->
    <div v-else class="rgf-grid">
      <div v-for="feature in selectedFeatures" :key="feature.featureId" class="rgf-card">
        <div class="rgf-card__media">
          <img v-if="feature.imageUri" :src="feature.imageUri" :alt="feature.name" loading="lazy"
               @error="onImageError(feature.featureId)"/>
          <div v-else class="rgf-card__placeholder"><i class="fa-solid fa-bell-concierge"></i></div>

          <span class="rgf-card__price">
            {{ formatRate(feature) }}
            <small v-if="feature.dailyPayment">/{{ msg('peer day') }}</small>
          </span>

          <div class="rgf-card__actions">
            <b-link class="rgf-act" v-b-tooltip :title="msg('Edit')" @click="editFeature(feature)">
              <i class="fas fa-pen"></i>
            </b-link>
            <b-link class="rgf-act rgf-act--danger" v-b-tooltip :title="msg('delete')" @click="removeFeature(feature)">
              <i class="fas fa-trash"></i>
            </b-link>
          </div>
        </div>

        <div class="rgf-card__body">
          <div class="rgf-card__name">{{ feature.name }}</div>
          <div v-if="feature.description" class="rgf-card__desc">{{ feature.description }}</div>

          <div class="rgf-card__tags">
            <span v-if="isOverridden(feature)" class="rgf-tag rgf-tag--accent" v-b-tooltip :title="msg('priceoverridehint')">
              <i class="fas fa-tag"></i>{{ msg('ownprice') }}
            </span>
            <span v-else class="rgf-tag" v-b-tooltip :title="msg('defaultpricehint')">
              <i class="fas fa-tag"></i>{{ msg('defaultprice') }}
            </span>
            <span v-if="feature.amountAllowed" class="rgf-tag"><i class="fas fa-layer-group"></i>{{ msg('Amount Allowed') }}</span>
            <span v-if="feature.autoApprove" class="rgf-tag rgf-tag--ok"><i class="fas fa-bolt"></i>{{ msg('Auto Approve') }}</span>
            <span v-if="feature.active === false" class="rgf-tag rgf-tag--muted"><i class="fas fa-ban"></i>{{ msg('Inactive') }}</span>
          </div>
        </div>
      </div>
    </div>

    <guest-feature-modal-real-estate ref="guestFeatureModal" @save="onNewFeature"/>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import GuestFeatureModalRealEstate from "@/components/modals/feature/GuestFeatureModalRealEstate.vue";

export default {
  name: "realEstateFeature",
  components: {GuestFeatureModalRealEstate},
  data: () => ({
    loading: true,
    allFeaturesMap: {},
    brokenImages: {},
  }),
  props: {
    buid: {
      required: true,
    },
    value: {
      required: true,
    }
  },
  mounted() {
    this.refresh();
  },
  computed: {
    features: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    selectedFeatures() {
      let $this = this;
      return (this.features || [])
          .filter(f => $this.allFeaturesMap[f.featureId] != null)
          .map(f => {
            const catalog = $this.allFeaturesMap[f.featureId];
            // catalog FIRST, the building's own row second: the old order was
            // {...f, ...catalog}, so the catalog's fields overwrote the price
            // staff had set here and it always rendered the default
            const merged = {...catalog, ...f};
            merged.rate = (f.rate != null && f.rate !== '') ? f.rate : catalog.defaultRate;
            merged.currency = f.currency || catalog.defaultCurrency;
            if ($this.brokenImages[f.featureId]) merged.imageUri = null;
            return merged;
          });
    }
  },
  methods: {
    ...mapActions('guestFeature', ['fetchGuestFeaturesByBuid']),
    refresh() {
      let $this = this;
      $this.loading = true;
      this.fetchGuestFeaturesByBuid({buid: this.buid}).then(data => {
        $this.allFeaturesMap = (data.Features || []).toObject('id');
      }).finally(() => {
        $this.loading = false;
      })
    },
    /** A price typed on this building, rather than the product's own default. */
    isOverridden(feature) {
      const row = (this.features || []).find(f => f.featureId === feature.featureId);
      return row != null && row.rate != null && row.rate !== '';
    },
    formatRate(feature) {
      if (feature.rate == null || feature.rate === '') return this.msg('nopriceset');
      const currency = feature.currency || 'EUR';
      try {
        return new Intl.NumberFormat('de-AT', {style: 'currency', currency}).format(feature.rate);
      } catch (e) {
        return `${feature.rate} ${currency}`;
      }
    },
    onImageError(featureId) {
      this.$set(this.brokenImages, featureId, true);
    },
    addNewFeature() {
      this.$refs.guestFeatureModal.addNewFeature(this.buid);
    },
    /** Reopen the picker on an existing row so the price can be changed. */
    editFeature(feature) {
      const row = (this.features || []).find(f => f.featureId === feature.featureId);
      this.$refs.guestFeatureModal.editFeature(this.buid, row, this.allFeaturesMap[feature.featureId]);
    },
    onNewFeature(feature) {
      let features = [...(this.features || [])];
      let currentIndex = features.findIndex(f => f.featureId === feature.featureId);
      if (currentIndex >= 0)
        features[currentIndex] = feature;
      else
        features.push(feature);
      this.features = features;
      this.$refs.guestFeatureModal.closeModal();
    },
    removeFeature(feature) {
      let features = [...(this.features || [])];
      let currentIndex = features.findIndex(f => f.featureId === feature.featureId);
      if (currentIndex >= 0)
        features.splice(currentIndex, 1);
      this.features = features;
    }
  }
}
</script>


<style lang="scss" scoped>
.rgf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 14px;
}

.rgf-card {
  border-radius: 14px;
  border: 1px solid rgba(34, 41, 47, .08);
  background: #fff;
  overflow: hidden;
  transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(34, 41, 47, .12);
    border-color: rgba(115, 103, 240, .35);

    .rgf-card__actions { opacity: 1; }
  }

  &__media {
    position: relative;
    height: 132px;
    background: linear-gradient(135deg, rgba(115, 103, 240, .1), rgba(115, 103, 240, .03));

    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }

  &__placeholder {
    height: 100%;
    display: flex; align-items: center; justify-content: center;
    font-size: 30px; color: #7367f0; opacity: .5;
  }

  &__price {
    position: absolute; left: 10px; bottom: 10px;
    padding: 4px 10px; border-radius: 999px;
    background: rgba(20, 18, 16, .78);
    color: #fff; font-size: 12.5px; font-weight: 800;
    backdrop-filter: blur(4px);

    small { font-weight: 600; opacity: .8; }
  }

  &__actions {
    position: absolute; right: 8px; top: 8px;
    display: flex; gap: 6px;
    opacity: 0;
    transition: opacity .18s ease;
  }

  &__body { padding: 12px 14px 14px; }

  &__name {
    font-weight: 800; font-size: 13.5px; line-height: 1.3;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  &__desc {
    margin-top: 3px;
    font-size: 12px; color: #6e6b7b; line-height: 1.4;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }

  &__tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
}

.rgf-act {
  width: 28px; height: 28px; border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, .92);
  color: #5e5873; font-size: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .18);

  &:hover { color: #7367f0; }
  &--danger:hover { color: #ea5455; }
}

.rgf-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 999px;
  font-size: 10.5px; font-weight: 700;
  background: rgba(34, 41, 47, .06); color: #6e6b7b;

  > i { font-size: 9px; }

  &--accent { background: rgba(115, 103, 240, .12); color: #7367f0; }
  &--ok { background: rgba(40, 199, 111, .12); color: #28c76f; }
  &--muted { background: rgba(234, 84, 85, .12); color: #ea5455; }
}

.rgf-empty {
  text-align: center;
  padding: 30px 16px;
  border: 1px dashed rgba(34, 41, 47, .16);
  border-radius: 14px;

  &__icon {
    width: 52px; height: 52px; margin: 0 auto 10px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 21px; color: #7367f0; background: rgba(115, 103, 240, .1);
  }

  &__title { font-weight: 800; margin: 0; font-size: 14px; }
  &__hint { color: #6e6b7b; font-size: 12.5px; margin: 4px 0 12px; }
}

.rgf-btn { border-radius: 10px; font-weight: 700; }

/* loading */
.rgf-card--skeleton { pointer-events: none; }
.rgf-skel {
  height: 10px; border-radius: 6px; margin: 10px 14px;
  background: rgba(34, 41, 47, .08);
  animation: rgf-pulse 1.2s ease-in-out infinite;

  &--title { width: 60%; }
  &--text { width: 85%; }
}
@keyframes rgf-pulse { 0%, 100% { opacity: 1 } 50% { opacity: .45 } }

/* dark */
.dark-layout {
  .rgf-card {
    background: #283046;
    border-color: rgba(255, 255, 255, .08);

    &:hover { box-shadow: 0 10px 26px rgba(0, 0, 0, .38); }
    &__desc { color: #b4b7bd; }
  }

  .rgf-act { background: rgba(40, 48, 70, .95); color: #d0d2d6; }

  .rgf-tag { background: rgba(255, 255, 255, .08); color: #b4b7bd; }

  .rgf-empty {
    border-color: rgba(255, 255, 255, .14);
    &__hint { color: #b4b7bd; }
  }

  .rgf-skel { background: rgba(255, 255, 255, .09); }
}
</style>
